import { db } from '../lib/firebase';
import { doc, updateDoc, getDoc, runTransaction, onSnapshot, collection, query, where, arrayUnion, increment } from 'firebase/firestore';
import { getWooCommerceConfig, syncProductStock, updateWooCommerceOrderStatus } from './woocommerce';
import { auth } from '../lib/firebase';
import { Order } from '../types';

interface StockUpdateResult {
  success: boolean;
  error?: string;
}

const ORDER_STATUSES = {
  pending: 'Pendente',
  preparation: 'Em preparação',
  cancelled: 'Cancelado',
  awaitingStock: 'Aguardando estoque',
  awaitingPickup: 'Aguardando retirada'
};

export async function updateProductStock(productId: string, quantity: number) {
  try {
    if (!productId) {
      return;
    }

    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);

    if (!productDoc.exists()) {
      return;
    }

    await updateDoc(productRef, {
      stockQuantity: increment(quantity)
    });

  } catch (error) {
    console.error('Erro ao atualizar stock:', error);
    throw error;
  }
}

export async function processOrderStock(order: Order, isCancel: boolean = false) {
  try {
    if (!order?.items) {
      return;
    }

    // Para pedidos do WooCommerce, verificar se os produtos existem no Lognow
    const isWooCommerceOrder = order.source === 'woocommerce';

    for (const item of order.items) {
      // Se for pedido do WooCommerce e o produto não existe no Lognow, pular
      if (isWooCommerceOrder && !item.lognowProductId) {
        continue;
      }

      const productId = isWooCommerceOrder ? item.lognowProductId : item.productId;
      
      if (!productId) {
        continue;
      }

      // Quantidade positiva para cancelamento (devolver ao estoque)
      // Quantidade negativa para nova venda (remover do estoque)
      const quantityChange = isCancel ? item.quantity : -item.quantity;

      try {
        await updateProductStock(productId, quantityChange);
      } catch (error) {
        // Ignorar erro e continuar com próximos produtos
      }
    }
  } catch (error) {
    console.error('Erro ao processar stock da encomenda:', error);
    // Não lançar o erro para não interromper o fluxo principal
  }
}

export async function handleOrderCancellation(order: Order) {
  try {
    await processOrderStock(order, true);
  } catch (error) {
    console.error('Erro ao processar cancelamento:', error);
  }
}

export async function handleOrderStatusChange(
  orderId: string,
  newStatus: string,
  oldStatus: string,
  companyId: string
): Promise<StockUpdateResult> {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    if (!orderSnap.exists()) {
      return { success: false, error: 'Pedido não encontrado' };
    }

    const order = { id: orderSnap.id, ...orderSnap.data() } as Order;

    // Se o pedido foi cancelado
    if (newStatus === 'cancelled' && oldStatus !== 'cancelled') {
      return await handleOrderCancellation(order);
    }

    // Se o pedido foi reativado (mudou de cancelado para outro status)
    if (oldStatus === 'cancelled' && newStatus !== 'cancelled') {
      return await processOrderStock(order);
    }

    // Se o pedido foi enviado ou entregue, não precisa fazer nada com o stock
    if (newStatus === 'shipped' || newStatus === 'delivered') {
      return { success: true };
    }

    return { success: true };
  } catch (error) {
    console.error('Error handling order status change:', error);
    return { success: false, error: 'Erro ao processar mudança de status' };
  }
}

// Listener para monitorar mudanças nas ordens
let unsubscribe: (() => void) | null = null;

export function setupOrderStatusListener(companyId: string) {
  if (unsubscribe) {
    unsubscribe();
  }

  const ordersRef = collection(db, 'orders');
  const q = query(ordersRef, where('companyId', '==', companyId));

  unsubscribe = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach(async (change) => {
      const order = { id: change.doc.id, ...change.doc.data() } as Order;

      if (change.type === 'added' && ['pending', 'preparation'].includes(order.status)) {
        await handleOrderStatusChange(order.id, order.status, '', order.companyId);
      } else if (change.type === 'modified') {
        const previousOrder = change.doc.metadata.hasPendingWrites ? null : change.doc.data() as Order;
        await handleOrderStatusChange(order.id, order.status, previousOrder?.status || '', order.companyId);
      }
    });
  });
}

export function stopOrdersListener() {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}
